<template>
  <div
    class="play-checkbox"
    :class="[`play-checkbox--${variant}`, { 'play-checkbox--switch': switchStyle, 'is-error': !!error, 'is-disabled': disabled }]"
  >
    <label class="play-checkbox__control">
      <input
        class="play-checkbox__native"
        type="checkbox"
        :checked="checked"
        :disabled="disabled"
        :aria-invalid="!!error"
        v-bind="$attrs"
        @change="onChange"
      >

      <!-- Switch track or checkbox box -->
      <span
        v-if="switchStyle"
        class="play-checkbox__switch"
        aria-hidden="true"
      ><span class="play-checkbox__knob" /></span>
      <span
        v-else
        class="play-checkbox__box"
        aria-hidden="true"
      >
        <svg
          class="play-checkbox__check"
          viewBox="0 0 24 24"
        ><path d="M5 12.5l4.2 4.2L19 6.5" /></svg>
      </span>

      <span
        v-if="$slots.default"
        class="play-checkbox__label"
      ><slot /></span>
    </label>

    <p
      v-if="error"
      class="play-checkbox__error"
    >
      {{ error }}
    </p>
  </div>
</template>

<script>
/**
 * PlayCheckbox — checkbox / toggle for the Play system.
 *
 * Token-driven (see src/styles/tokens.scss): no hardcoded colors. Replaces
 * bootstrap-vue's <b-form-checkbox>. A visually-hidden native <input> drives
 * real keyboard + form semantics; the box/switch is the styled visual.
 *
 * Default slot is the (rich) label — links allowed, e.g. the booking "I agree
 * to Privacy & Terms" line. `switch` renders a toggle instead of a box.
 * `error` shows the danger state + message.
 *
 * VUE3-MIGRATION: `model: { prop: 'checked', event: 'change' }` — Vue 3 uses
 * `modelValue` / `update:modelValue`. Change this block + the emit name on
 * migration. $attrs (id/name/…) already fall through to the native input.
 */
export default {
  name: 'PlayCheckbox',
  inheritAttrs: false,
  model: {
    prop: 'checked',
    event: 'change',
  },
  props: {
    checked: {
      type: Boolean,
      default: false,
    },
    /** Toggle-switch visual instead of a checkbox box */
    switch: {
      type: Boolean,
      default: false,
    },
    variant: {
      type: String,
      default: 'primary',
      validator: v => ['primary', 'secondary', 'highlight', 'accent'].includes(v),
    },
    /** Error message — its presence drives the invalid styling */
    error: {
      type: String,
      default: '',
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    // `switch` is a reserved word — alias for use in template/styles
    switchStyle() {
      return this.switch
    },
  },
  methods: {
    onChange(e) {
      this.$emit('change', e.target.checked)
    },
  },
}
</script>

<style lang="scss" scoped>
.play-checkbox {
  --cb-on: var(--c-primary);
  --cb-on-strong: var(--c-primary-strong);

  &--secondary { --cb-on: var(--c-secondary); --cb-on-strong: var(--c-secondary-strong); }
  &--highlight { --cb-on: var(--c-highlight); --cb-on-strong: var(--c-highlight-strong); }
  &--accent    { --cb-on: var(--c-accent);    --cb-on-strong: var(--c-accent-strong); }

  &__control {
    display: inline-flex;
    align-items: flex-start;
    gap: 11px;
    cursor: pointer;
    -webkit-tap-highlight-color: transparent;
  }

  /* Hidden but accessible native input */
  &__native {
    position: absolute;
    width: 1px;
    height: 1px;
    opacity: 0;
    margin: 0;
    pointer-events: none;
  }

  /* ── Checkbox box ── */
  &__box {
    flex-shrink: 0;
    width: 24px;
    height: 24px;
    display: grid;
    place-items: center;
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-sm);
    box-shadow: 0 3px 0 var(--border-ink);
    transition:
      transform var(--dur-fast) var(--ease-bounce),
      box-shadow var(--dur-fast) var(--ease-out),
      background var(--dur-fast) var(--ease-out);
  }

  &__check {
    width: 16px;
    height: 16px;
    fill: none;
    stroke: var(--c-primary-on);
    stroke-width: 3.4;
    stroke-linecap: round;
    stroke-linejoin: round;
    stroke-dasharray: 24;
    stroke-dashoffset: 24;
    transition: stroke-dashoffset var(--dur) var(--ease-bounce);
  }

  /* ── Switch ── */
  &__switch {
    flex-shrink: 0;
    position: relative;
    width: 46px;
    height: 26px;
    background: var(--surface-card);
    border: var(--border-w) solid var(--border-ink);
    border-radius: var(--radius-pill);
    transition: background var(--dur-fast) var(--ease-out);
  }

  &__knob {
    position: absolute;
    top: 50%;
    left: 3px;
    width: 16px;
    height: 16px;
    transform: translateY(-50%);
    background: var(--border-ink);
    border-radius: 50%;
    transition: left var(--dur) var(--ease-bounce), background var(--dur-fast) var(--ease-out);
  }

  &__label {
    font-family: var(--font-body);
    font-weight: 500;
    color: var(--text-body);
    line-height: 1.45;
    padding-top: 1px;

    ::v-deep a {
      color: var(--cb-on-strong);
      font-weight: 600;
      text-decoration: underline;
    }
  }

  &__error {
    margin: 6px 0 0;
    font-family: var(--font-body);
    font-size: 12.5px;
    font-weight: 500;
    color: var(--c-danger-strong);
  }

  /* ── Checked ── */
  &__native:checked ~ .play-checkbox__box {
    background: var(--cb-on);
    box-shadow: 0 3px 0 var(--cb-on-strong);

    .play-checkbox__check { stroke-dashoffset: 0; }
  }

  &__native:checked ~ .play-checkbox__switch {
    background: var(--cb-on);

    .play-checkbox__knob { left: 23px; background: var(--c-primary-on); }
  }

  /* ── Hover / focus / active ── */
  &__control:hover .play-checkbox__box { box-shadow: 0 4px 0 var(--border-ink); }
  &__control:hover .play-checkbox__native:checked ~ .play-checkbox__box { box-shadow: 0 4px 0 var(--cb-on-strong); }
  &__control:active .play-checkbox__box { transform: translateY(2px); box-shadow: var(--shadow-press); }

  &__native:focus-visible ~ .play-checkbox__box,
  &__native:focus-visible ~ .play-checkbox__switch {
    outline: 2px solid var(--c-secondary);
    outline-offset: 2px;
  }

  /* ── Error ── */
  &.is-error .play-checkbox__box { border-color: var(--c-danger); box-shadow: 0 3px 0 var(--c-danger-strong); }

  /* ── Disabled ── */
  &.is-disabled {
    opacity: .55;

    .play-checkbox__control { cursor: not-allowed; }
  }
}

@media (prefers-reduced-motion: reduce) {
  .play-checkbox__box,
  .play-checkbox__check,
  .play-checkbox__knob { transition: none; }
}
</style>
